import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, ArrowUpRight, ArrowDownLeft, CreditCard, TrendingUp, TrendingDown } from 'lucide-react';
import { LockChip } from './LockChip';
import { Transaction } from '../App';

interface TokenDetailsProps {
  token: {
    symbol: string;
    name: string;
    balance: number;
    value: number;
    change: number;
    icon: string;
  };
  transactions: Transaction[];
  /** 락업된 수량 (볼트에 잠겨 있어 아직 옮길 수 없는 물량) */
  lockedAmount?: number;
  /** 락업 해제 일정. 날짜와 수량만. */
  lockSchedule?: { date: string; amount: number }[];
  /** 락업을 뺀, 지금 쓸 수 있는 수량 */
  availableAmount?: number;
  /** 락업으로 지급된 건들 (지갑 Transfer 가 안 남아 거래내역에 안 잡힌다) */
  grants?: { kind: string; amount: number; txHash: string; date: string; locked: boolean; usdt: number | null }[];
  onBack: () => void;
  onSend: () => void;
  onReceive: () => void;
  onBuy: () => void;
}

export function TokenDetails({ token, transactions, lockedAmount = 0, lockSchedule = [], availableAmount = 0, grants = [], onBack, onSend, onReceive, onBuy }: TokenDetailsProps) {
  console.log('🎨 TokenDetails - 전달받은 transactions:', transactions?.length || 0);
  console.log('🎨 TokenDetails - transactions 배열:', Array.isArray(transactions));
  
  // [2026-09-11 버그수정] 여기로 넘어오는 블록체인 조회 결과는 전부 wHANDY 거래인데,
  // 예전 코드는 필터 없이 그대로 뿌려서 USDT 상세화면에도 wHANDY 입금건이 "+6000 USDT"로
  // 잘못 표시되고 환산액은 NaN이 나왔다. wHANDY일 때만 내역을 보여준다.
  const isHandyToken = (token.symbol || '').toUpperCase().includes('HANDY');
  const tokenTransactions = isHandyToken ? (transactions || []) : [];

  /**
   * 락업 지급은 토큰이 금고로 가서 지갑 Transfer 가 안 남는다.
   * 그대로 두면 20 USDT 내고 받은 사람 화면에 아무것도 안 떠서 "안 들어왔다"가 된다.
   * 온체인 내역과 같은 목록에 섞되, 락업 건임을 분명히 표시한다.
   */
  const grantRows = isHandyToken ? grants : [];

  // balance가 0이면 0으로 나눠 NaN이 되므로 단가를 안전하게 계산한다.
  const unitPrice = token.balance > 0 ? token.value / token.balance : 0;

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={onBack}
            className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">{token.name}</h1>
            <div className="flex items-center justify-center gap-1.5">
              <p className="text-muted-foreground text-sm">{token.symbol}</p>
              <LockChip
                locked={lockedAmount}
                available={availableAmount}
                schedule={lockSchedule}
                symbol={token.symbol}
              />
            </div>
          </div>
          <div className="w-10 h-10"></div>
        </div>

        {/* Token Balance Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              {token.icon}
            </div>
            <div className="mb-4">
              <p className="text-3xl font-light mb-2">{token.balance.toFixed(2)}</p>
              <p className="text-white/80 text-lg">{token.symbol}</p>
            </div>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <p className="text-2xl font-bold">${token.value.toFixed(2)}</p>
              {token.change >= 0 ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
            </div>
            <p className={`text-sm ${token.change >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {token.change >= 0 ? '+' : ''}{token.change.toFixed(2)}% Today
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons - 전부 오픈 예정(2026-09-11). Tailwind 퍼지에 걸리지 않도록
            색상은 인라인 style로 고정한다. */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'send', label: '보내기', icon: <ArrowUpRight className="h-6 w-6" />, onClick: onSend },
            { key: 'receive', label: '받기', icon: <ArrowDownLeft className="h-6 w-6" />, onClick: onReceive },
            { key: 'buy', label: '구매', icon: <CreditCard className="h-6 w-6" />, onClick: onBuy },
          ].map(act => (
            <button
              key={act.key}
              onClick={act.onClick}
              className="p-4 rounded-2xl flex flex-col items-center space-y-1 shadow-sm"
              style={{ background: '#E5E7EB', color: '#9CA3AF' }}
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#D1D5DB' }}>
                {act.icon}
              </div>
              <span className="font-medium text-sm">{act.label}</span>
              <span className="text-[10px]" style={{ color: '#6B7280' }}>오픈 예정</span>
            </button>
          ))}
        </div>

        {/* Transaction History */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground">Transaction History</h3>
              <span className="text-muted-foreground text-sm">{tokenTransactions.length + grantRows.length} transactions</span>
            </div>

            {/* 락업 지급 — 지갑 Transfer 가 없어 아래 온체인 목록엔 안 잡히는 건들 */}
            {grantRows.length > 0 && (
              <div className="space-y-3 mb-3">
                {grantRows.map((g, i) => (
                  <div key={(g.txHash || '') + i}
                       className="flex items-center justify-between p-3 rounded-xl"
                       style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                           style={{ background: '#FEF3C7' }}>🔒</div>
                      <div>
                        <p className="text-foreground font-medium">
                          {g.kind === 'purchase' ? '구매 지급' : '보너스 지급'}
                          {g.usdt != null && <span className="text-muted-foreground"> · {g.usdt} USDT</span>}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {new Date(g.date).toLocaleDateString('ko-KR',
                            { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs" style={{ color: '#B45309' }}>
                          {g.locked ? '락업 보관 중 · 해제일부터 출금 가능' : '지갑 지급 완료'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold" style={{ color: '#B45309' }}>
                        +{g.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-muted-foreground">{token.symbol}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tokenTransactions.length === 0 && grantRows.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">{token.icon}</span>
                </div>
                <p className="text-muted-foreground">No {token.symbol} transactions yet</p>
                <p className="text-muted-foreground/60 text-sm mt-1">Your {token.symbol} activity will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-green-500 mb-2">✅ 거래 {tokenTransactions.length}건</p>
                {tokenTransactions.map((tx, index) => {
                  // 블록체인 데이터 사용 (hash, from, to, value, timestamp)
                  const isReceived = tx.to === tx.to; // 일단 모든 거래 표시
                  const amount = parseFloat(tx.value || tx.amount?.toString() || '0');
                  const txHash = tx.hash || `tx-${tx.id}`;
                  
                  return (
                    <div key={txHash + index} className="flex items-center justify-between p-3 bg-muted rounded-xl hover:bg-accent transition-colors cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {amount > 0 ? (
                            <ArrowDownLeft className="h-5 w-5 text-green-600" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-foreground font-medium">
                            {tx.from?.substring(0, 10)}... → {tx.to?.substring(0, 10)}...
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {tx.timestamp 
                              ? new Date(tx.timestamp * 1000).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : new Date(tx.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                            }
                          </p>
                          <p className="text-xs text-blue-500">블록 #{tx.blockNumber || 'Pending'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {amount > 0 ? '+' : ''}{amount.toFixed(2)} {token.symbol}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ≈ ${Math.abs(amount * unitPrice).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}